import { config } from "../config";

export async function deleteBusiness(
  id: number,
  token: string | null
): Promise<any> {
  try {
    if (!token) {
      console.warn("Authorization token is missing. Skipping request.");
      return null;
    }
    const response = await axios.delete(
      `${config.api.baseUrl}/businesses/${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Помилка видалення бізнесу:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
}
import axios from "axios";

export interface Business {
  id: string;
  name: string;
  categories: string[];
  address: string;
  phone: string;
  rating: number;
  image: string;
  is_master_business?: boolean; // Нове поле!
  experience?: string; // Досвід майстра
  // services: string[]; // Commented out - not needed for business cards
}

export interface BusinessesResponse {
  businesses: Business[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function createBusiness(
  data: any,
  token: string | null
): Promise<any> {
  try {
    console.log("Authorization token before request:", token);
    if (!token) {
      console.warn("Authorization token is missing. Skipping request.");
      return null;
    }

    console.log("Sending request to create business with data:", data);
    try {
      const response = await axios.post(
        `${config.api.baseUrl}/businesses`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Received response:", response.status, response.data);
      return response.data;
    } catch (error: any) {
      console.error("Помилка створення бізнесу:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      throw error;
    }
  } catch (error) {
    console.error("Помилка створення бізнесу:", error);
    throw error;
  }
}

export async function getBusinesses(
  city?: string,
  page: number = 1,
  limit: number = 12,
  options?: { signal?: AbortSignal; language?: string }
): Promise<BusinessesResponse> {
  try {
    const params: any = { page, limit };
    // Завжди передаємо місто - або передане, або Київ за замовчуванням
    if (city) {
      params.city = city;
    } else {
      params.city = 'Київ';
    }
    // Додаємо параметр мови, якщо вказано
    if (options?.language) {
      params.lang = options.language;
    }
    const response = await axios.get(`${config.api.baseUrl}/businesses`, {
      params,
      signal: options?.signal,
    });
    // Підставляємо logo_url у image для кожного бізнесу
    if (response.data && Array.isArray(response.data.businesses)) {
      response.data.businesses = response.data.businesses
        // Фільтруємо тільки звичайні бізнеси (is_master_business = false або undefined)
        .filter((b: any) => !b.is_master_business)
        .map((b: any) => ({
          ...b,
          image: b.logo_url || b.image || null,
        }));
    }
    return response.data;
  } catch (error: any) {
    if (error.name === "AbortError" || error.name === "CanceledError") {
      // Request was aborted, return empty result
      return { businesses: [], total: 0, page, limit, totalPages: 0 };
    }
    console.error("Помилка отримання бізнесів:", error);
    return { businesses: [], total: 0, page, limit, totalPages: 0 };
  }
}

export async function updateBusiness(
  data: any,
  token: string | null
): Promise<any> {
  try {
    if (!token) {
      console.warn("Authorization token is missing. Skipping request.");
      return null;
    }
    // Формуємо payload у snake_case та тільки потрібні поля
    const payload: any = {
      id: data.id,
      name: data.name || data.business_name,
      description: data.description,
      phone: data.phone,
      logo_url: data.logo_url,
      category_ids: data.categoryIds,
      services: data.services,
      city: data.city,
      street: data.street,
      building: data.building,
      is_active: data.is_active,
      is_master_business: data.is_master_business, // Додано поле!
      experience: data.experience, // Досвід майстра
    };
    // Видаляємо undefined/null поля
    Object.keys(payload).forEach(
      (key) =>
        (payload[key] === undefined || payload[key] === null) &&
        delete payload[key]
    );
    console.log("[DEBUG] Payload for updateBusiness:", JSON.stringify(payload));
    const response = await axios.put(
      `${config.api.baseUrl}/business`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Помилка оновлення бізнесу:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
}

export async function getBusinessSettings(token: string | null): Promise<any> {
  try {
    if (!token) {
      console.warn("Authorization token is missing. Skipping request.");
      return null;
    }
    const response = await axios.get(`${config.api.baseUrl}/business`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Transform snake_case to camelCase
    const data = response.data;
    if (Array.isArray(data)) {
      return data.map((business) => ({
        ...business,
        categoryIds: business.category_ids,
        business_name: business.name,
        business_phone: business.phone,
      }));
    }
    return data;
  } catch (error: any) {
    console.error("Помилка отримання бізнесу:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return null;
  }
}

export async function getBusinessesByOwner(
  ownerId: string,
  token: string | null
): Promise<BusinessesResponse> {
  try {
    if (!token) {
      console.warn("Authorization token is missing. Skipping request.");
      return { businesses: [], total: 0, page: 1, limit: 12, totalPages: 0 };
    }
    const params: any = { owner_id: ownerId };
    const response = await axios.get(`${config.api.baseUrl}/businesses`, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Помилка отримання бізнесів за owner_id:", error);
    return { businesses: [], total: 0, page: 1, limit: 12, totalPages: 0 };
  }
}
